import React, { useEffect, useRef, useState } from 'react';
import { Button, Image, Spin } from 'antd';
import { fetchPreviewPdf, fileKind, loadPdfLibrary, pdfAssetOptions, previewError, safeFileUrl } from '../../utils/pdfPreview';
import './index.css';

function PdfPreview({ url }) {
  const [document, setDocument] = useState(null);
  const [error, setError] = useState('');
  const [pageNumber, setPageNumber] = useState(1);
  const [rendering, setRendering] = useState(false);
  const [attempt, setAttempt] = useState(0);
  const [width, setWidth] = useState(600);
  const canvasRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    let active = true;
    let loadingTask;
    const controller = new AbortController();
    setDocument(null); setError(''); setPageNumber(1);
    (async () => {
      const [pdfjs, data] = await Promise.all([loadPdfLibrary(), fetchPreviewPdf(url, controller.signal)]);
      if (!active) return;
      loadingTask = pdfjs.getDocument({ data, ...pdfAssetOptions });
      const pdf = await loadingTask.promise;
      if (active) setDocument(pdf);
    })().catch((failure) => { if (active) setError(previewError(failure)); });
    return () => {
      active = false;
      controller.abort();
      if (loadingTask) Promise.resolve(loadingTask.destroy()).catch(() => {});
    };
  }, [url, attempt]);

  useEffect(() => {
    const node = containerRef.current;
    if (!node) return undefined;
    const update = () => setWidth(Math.max(100, node.clientWidth - 24));
    update();
    if (typeof ResizeObserver === 'undefined') {
      window.addEventListener('resize', update);
      return () => window.removeEventListener('resize', update);
    }
    const observer = new ResizeObserver(update);
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!document || !canvasRef.current) return undefined;
    let active = true;
    let renderTask;
    const canvas = canvasRef.current;
    setRendering(true);
    (async () => {
      const page = await document.getPage(pageNumber);
      if (!active) return;
      const original = page.getViewport({ scale: 1 });
      // Keep one page/canvas alive; cap pixel count to avoid GPU/memory exhaustion.
      const scale = Math.min(width / original.width, 2, Math.sqrt(4000000 / (original.width * original.height)));
      const viewport = page.getViewport({ scale });
      canvas.width = Math.ceil(viewport.width);
      canvas.height = Math.ceil(viewport.height);
      renderTask = page.render({ canvasContext: canvas.getContext('2d'), viewport, background: '#ffffff' });
      await renderTask.promise;
      page.cleanup();
      if (active) setRendering(false);
    })().catch((failure) => {
      if (active && failure.name !== 'RenderingCancelledException') {
        setError(previewError(failure)); setRendering(false);
      }
    });
    return () => {
      active = false;
      renderTask?.cancel();
      canvas.width = 0; canvas.height = 0;
    };
  }, [document, pageNumber, width]);

  return <div className="pai-pdf-preview" ref={containerRef}>
    {error ? <div className="pai-preview-message" role="alert">
      <p>{error}</p>
      <Button onClick={() => setAttempt((value) => value + 1)}>Retry preview</Button>
    </div> : <>
      {document && <div className="pai-preview-pagination">
        <Button disabled={pageNumber <= 1 || rendering} onClick={() => setPageNumber((value) => value - 1)}>Previous</Button>
        <span>Page {pageNumber} / {document.numPages}</span>
        <Button disabled={pageNumber >= document.numPages || rendering} onClick={() => setPageNumber((value) => value + 1)}>Next</Button>
      </div>}
      {(!document || rendering) && <div role="status" className="pai-preview-loading"><Spin /> Loading preview…</div>}
      <canvas ref={canvasRef} aria-label={`PDF page ${pageNumber}`} style={{ display: document ? 'block' : 'none', visibility: rendering ? 'hidden' : 'visible' }} />
    </>}
  </div>;
}

function ImagePreview({ url, fileName }) {
  const [failed, setFailed] = useState(false);
  return failed ? <div className="pai-preview-message" role="alert">Unable to load the image. Download or open the original file.</div>
    : <Image src={url} alt={fileName} style={{ maxWidth: '100%' }} onError={() => setFailed(true)} />;
}

export default function FilePreview({ url, fileName = '', active = true }) {
  const safeUrl = safeFileUrl(url);
  if (!active) return null;
  if (!safeUrl) return <div className="pai-preview-message" role="alert">No valid file URL is available.</div>;
  const kind = fileKind(fileName, safeUrl);
  return <div className="pai-file-preview">
    <div className="pai-preview-actions">
      <a href={safeUrl} target="_blank" rel="noopener noreferrer">Open in new window</a>
      <a href={safeUrl} target="_blank" rel="noopener noreferrer" download={fileName || undefined}>Download</a>
    </div>
    {kind === 'pdf' ? <PdfPreview key={safeUrl} url={safeUrl} />
      : kind === 'image' ? <ImagePreview key={safeUrl} url={safeUrl} fileName={fileName} />
        : <div className="pai-preview-message">This file type cannot be previewed here. Download or open the original file.</div>}
  </div>;
}
